import {
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Switch,
  Tabs,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { WidgetPresence } from 'lib-shared-legacy-types';
import { ChangeEvent, Component } from 'react';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import ColorField from 'components/shared/util/ColorField';
import ReduxFormDixaSwitch from 'components/shared/util/DixaSwitch';
import InputField from 'components/shared/util/InputField';
import SelectField from 'components/shared/util/SelectField';
import { getRequestID } from 'components/shared/util/request';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import DemoWidget from '../../containers/demoWidget';
import type {
  WidgetCustomizationDispatchPropsT,
  WidgetCustomizationStatePropsT,
} from '../../containers/modals/widgetcustomization';
import type {
  FlowChatNodeWidgetCustomizationPayloadT,
  FlowChatNodeWidgetCustomizationT,
  LocalizationsTypeT,
  ThemeT,
} from '../nodeTypes';
import type { LocalizationsT, Positions, Views } from '../types';

import styles from './widgetCustomization.module.scss';

export type PropsT =
  FlowManagerEditModalComponentT<FlowChatNodeWidgetCustomizationT> &
    WidgetCustomizationStatePropsT &
    WidgetCustomizationDispatchPropsT &
    InjectedProps;

type InjectedProps = InjectedFormProps<
  WidgetCustomizationModalFormValuesT,
  PropsT
>;

export type WidgetCustomizationModalFormValuesT =
  FlowChatNodeWidgetCustomizationPayloadT;

type ValidationT = {
  payload?: Partial<FlowChatNodeWidgetCustomizationPayloadT>;
};

export type iFramePropT = {
  apiText: string;
  stateText: string;
  targetValue?: string;
  contactText?: string;
};

type StateT = {
  selectedLocale: string;
  selectedView?: null | string;
  show_contact_form: boolean;
  show_widget: boolean;
  view?: Views;
  widgetType: string;
  selectedTab: string;
};

export const FORM_NAME = 'chatFlowCustomForm';
/**
 * https://stackoverflow.com/questions/56551644/property-contentwindow-does-not-exist-on-type-htmlelement-sending-from-one-s
 * type guard heck to check if HTMLElement is iframe
 */
export const isIframe = (
  input: HTMLElement | null,
): input is HTMLIFrameElement => input !== null && input.tagName === 'IFRAME';

const validate = (values: WidgetCustomizationModalFormValuesT): FormErrors => {
  const errors: ValidationT = {};
  const localeTypes: LocalizationsT[] = [
    'ar',
    'cs-CZ',
    'da',
    'de-DE',
    'el-GR',
    'en-US',
    'es-ES',
    'et-EE',
    'fi',
    'fr-FR',
    'hi-IN',
    'hr-HR',
    'hu-HU',
    'id-ID',
    'is',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'lt-LT',
    'lv-LV',
    'ms-MY',
    'nl',
    'no',
    'pl-PL',
    'pt-PT',
    'ro-RO',
    'ru-RU',
    'sv',
    'th-TH',
    'tr-TR',
    'vi-VN',
    'zh-CN',
  ];

  if (!values) {
    return errors;
  }

  for (const local of localeTypes) {
    if (
      !values.localizations ||
      !values.localizations[local] ||
      !values.localizations[local].minimized.html_text_online
    ) {
      errors.payload = {
        [local]: {
          minimized: {
            html_text_online: 'Required',
          },
        },
      };
    }
    if (
      !values.localizations ||
      !values.localizations[local] ||
      !values.localizations[local].minimized.html_text_offline
    ) {
      errors.payload = {
        [local]: {
          minimized: {
            html_text_online: 'Required',
          },
        },
      };
    }
  }

  return errors;
};

const tabs = [
  { id: 'translations', label: 'Translations' },
  { id: 'theme', label: 'Theme / Color' },
  { id: 'widgetPosition', label: 'Widget position' },
];

class WidgetCustomizeModal extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);

    const {
      node: { payload },
    } = this.props;

    this.state = {
      selectedLocale: '',
      selectedView: null,
      show_contact_form: false,
      show_widget: false,
      view: payload?.medium?.minimized_view.view,
      widgetType: 'medium',
      selectedTab: 'translations',
    };
  }

  componentDidMount() {
    if (this.props.node.nodeType === FlowNodeTypes.WIDGET_CUSTOMIZATION) {
      this.props.nodeWidgetCustomizationLoadPayload({ node: this.props.node });
    }
  }

  @boundMethod
  handleSaveClick(values: WidgetCustomizationModalFormValuesT): void {
    const { node, replaceNode, triggerClose, destroy, resetLocalization } =
      this.props;
    const { view } = this.state;

    const payload: FlowChatNodeWidgetCustomizationPayloadT = {
      ...values,
      medium: {
        ...values.medium,
        minimized_view: {
          position: values.medium.minimized_view.position,
          view: view || 'badge',
        },
      },
      nextNodeId: node.payload?.nextNodeId || getRequestID(),
    };

    const nodeOptions: FlowChatNodeWidgetCustomizationT = {
      ...node,
      badges: [],
      icon: 'i-widget-customize',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.WIDGET_CUSTOMIZATION,
      overlay: [],
      payload,
      text: 'Widget Customization',
    };

    replaceNode(nodeOptions);
    triggerClose();
    destroy();
    resetLocalization();
  }

  @boundMethod
  changeWidgetTypeShown(widgetType: string): void {
    this.setState({ widgetType }, () => {
      const { currentFormValues } = this.props;

      if (currentFormValues[widgetType]?.minimized_view) {
        const position: Positions =
          currentFormValues[widgetType].minimized_view.position;
        this.changeWidgetPosition(widgetType, position);
      }
    });
  }

  @boundMethod
  changeSelectedView(view: string): void {
    if (view === 'minimized') {
      view = 'minimized_view';
    }
    if (view === 'conversation') {
      view = 'chatbox_view';
    }
    this.setState({ selectedView: view });
  }

  @boundMethod
  changeSelectedLanguage(selectedLocale: string): void {
    this.setState({ selectedLocale });
  }

  @boundMethod
  changeWidgetState(payload: Partial<WidgetPresence>): void {
    if (payload && payload.show_widget) {
      this.setState({ show_widget: payload.show_widget });
    }
    if (payload && payload.show_contact_form) {
      this.setState({ show_contact_form: payload.show_contact_form });
    }
  }

  @boundMethod
  sendToIframeSmall(iFrameProps: iFramePropT): void {
    if (document) {
      const ID = 'demo-widget-iframe-wrapper-small';
      this.sendToDixa(ID, iFrameProps);
    }
  }

  @boundMethod
  sendToIframeMedium(iFrameProps: iFramePropT): void {
    if (document) {
      const ID = 'demo-widget-iframe-wrapper-medium';
      this.sendToDixa(ID, iFrameProps);
    }
  }

  @boundMethod
  sendToDixa(id: string, iFrameProp: iFramePropT): void {
    const iframe = document.getElementById(id);

    if (isIframe(iframe) && iframe.contentWindow) {
      iframe.contentWindow._dixa(
        iFrameProp.apiText,
        iFrameProp.stateText,
        iFrameProp.targetValue,
        iFrameProp.contactText,
      );
    }
  }

  @boundMethod
  changeConversationMinimizedHandler(
    event: ChangeEvent<HTMLInputElement>,
  ): void {
    // We use the javascript API to talk with the widget in the iframe.
    // And change the minimized text.
    if (!this.state.selectedLocale) {
      console.error('state selectedLocale is empty string');
      return;
    }

    const iFrameProps = {
      apiText: 'api.setMinimizedText',
      stateText: this.state.selectedLocale,
      targetValue: event.target.value,
    };

    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  @boundMethod
  changeContactFormMinimizedHandler(
    event: ChangeEvent<HTMLInputElement>,
  ): void {
    // We use the javascript API to talk with the widget in the iframe.
    // And change the minimized text.
    if (!this.state.selectedLocale) {
      console.error('state selectedLocale is empty');
      return;
    }

    const iFrameProps = {
      apiText: 'api.setMinimizedText',
      stateText: this.state.selectedLocale,
      targetValue: event.target.value,
      cantactText: 'contact',
    };

    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  @boundMethod
  changeLocalization(checked: boolean, local: LocalizationsT): void {
    if (checked) {
      this.props.removeLocalization({ local });
      this.changeLanguage('en-US');
    } else {
      this.props.addLocalization({ local });
      this.changeLanguage(local);
    }
  }

  @boundMethod
  changeWidgetColor(color: string): void {
    const theme: ThemeT = {
      ...this.props.payload.theme,
      color,
    };
    const iFrameProps = {
      apiText: 'api.setTheme',
      stateText: JSON.stringify(theme),
    };

    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  @boundMethod
  changeWidgetPosition(widgetType: string, position: string): void {
    if (!this.state.selectedView) {
      console.error('state selectedView is not given.');
      return;
    }
    const iFrameProps = {
      apiText: 'api.setPosition',
      stateText: position,
      targetValue: this.state.selectedView || undefined,
    };

    if (widgetType === 'medium') {
      this.sendToIframeMedium(iFrameProps);
      this.props.change('medium.chatbox_view.position', position);
    } else {
      this.sendToIframeSmall(iFrameProps);
      this.props.change('small.chatbox_view.position', position);
    }
  }

  @boundMethod
  handleStickyChange(isChecked: boolean): void {
    const view = isChecked ? 'sticky' : 'badge';

    this.setState({ view }, () => {
      const iFrameProps = {
        apiText: 'api.setSticky',
        stateText: view,
      };
      this.sendToIframeMedium(iFrameProps);
    });
  }

  @boundMethod
  changeLanguage(localValue: string): void {
    const iFrameProps = {
      apiText: 'api.setLanguage',
      stateText: localValue,
    };
    this.changeSelectedLanguage(localValue);
    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  @boundMethod
  closeModal() {
    const { destroy, resetLocalization, triggerClose } = this.props;
    destroy();
    resetLocalization();
    triggerClose();
  }

  render() {
    const { payload, node, nodeLabel, handleSubmit } = this.props;
    const widgetSettingsLoaded = payload?.loaded;
    const { view } = this.state;

    const tabsBody = {
      translations: (
        <div>
          <div>
            <label className={styles.languageTitle}>
              Supported Languages in the widget
            </label>
            <div className={styles.supportedLanguages}>
              {this.props.localizationOptions.map(
                (local: LocalizationsTypeT, i: number) => {
                  if (local.value === 'en-US') return null;
                  const checked = this.props.selectedLocalizations.some(
                    (selectedLocal: LocalizationsTypeT) =>
                      selectedLocal.value === local.value,
                  );

                  return (
                    <div key={i} className={styles.languageItem}>
                      <label>Disabled</label>
                      <Switch
                        onChange={() => {
                          this.changeLocalization(checked, local.value);
                        }}
                        checked={checked}
                        name=""
                        value=""
                      />
                      <label>{`Enabled ${local.label}`}</label>
                    </div>
                  );
                },
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="">Select Language</label>
            <select
              className="select form-control"
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                this.changeLanguage(e.target.value);
              }}
              value={this.state.selectedLocale}
            >
              <option hidden value="">
                {' '}
                - Select a language
              </option>
              {this.props.selectedLocalizations.map(
                (option: { value: string; label: string }, index: number) => {
                  return (
                    <option value={option.value} key={index}>
                      {option.label}
                    </option>
                  );
                },
              )}
              ;
            </select>
          </div>

          {this.state.selectedLocale !== '' ? (
            <Field
              displayName="Conversation form minimized"
              onChangeAction={this.changeConversationMinimizedHandler}
              name={`localizations.${this.state.selectedLocale}.minimized.html_text_online`}
              type="text"
              className="form-control"
              component={InputField}
            />
          ) : null}

          {this.state.selectedLocale !== '' ? (
            <Field
              displayName="Contact form minimized"
              onChangeAction={this.changeContactFormMinimizedHandler}
              name={`localizations.${this.state.selectedLocale}.minimized.html_text_offline`}
              type="text"
              className="form-control"
              component={InputField}
            />
          ) : null}
        </div>
      ),
      theme: (
        <div>
          <Field
            label="Theme color"
            name="theme.color"
            onChangeAction={this.changeWidgetColor}
            className="form-control theme-color"
            component={ColorField}
          />
          <br />
          <br />
          <br />
          <br />
          <br />
          <br />
          <br />
          <br />
        </div>
      ),
      widgetPosition: (
        <div>
          <Field
            displayName="Mobile widget position"
            name="small.minimized_view.position"
            className="form-control"
            onChangeAction={(position: string) => {
              this.changeWidgetPosition('small', position);
            }}
            component={SelectField}
          >
            <option hidden value="">
              {' '}
              - Select a position
            </option>
            {this.props.mobilePositionOptions.map(
              (option: { value: string; label: string }, index: number) => {
                return (
                  <option value={option.value} key={index}>
                    {option.label}
                  </option>
                );
              },
            )}
            ;
          </Field>

          <Field
            displayName="Desktop widget position"
            name="medium.minimized_view.position"
            className="form-control"
            onChangeAction={(position: string) => {
              this.changeWidgetPosition('medium', position);
            }}
            component={SelectField}
          >
            <option hidden value="">
              {' '}
              - Select a position
            </option>
            {this.props.desktopPositionOptions.map(
              (option: { value: string; label: string }, index: number) => {
                return (
                  <option value={option.value} key={index}>
                    {option.label}
                  </option>
                );
              },
            )}
            ;
          </Field>

          <Field
            displayName="Sticky position"
            labelB="Enabled"
            labelA="Disabled"
            onChangeAction={this.handleStickyChange}
            name="medium.minimized_view.view"
            className="form-control"
            isChecked={view === 'sticky'}
            component={ReduxFormDixaSwitch}
            controlled={true}
            showHelpIcon={true}
            helpIconTitle="Sticky position"
            helpIconText="Whether the widget should stick the edge of the page."
          />
        </div>
      ),
    };

    return (
      <div className="localization modal-chatflow-holder chat-modal-customize-widget">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.closeModal} />
          <ModalBody>
            <FlexBox
              flexDirection="column"
              gap="300"
              className="chat-modal-customize-widget__content"
            >
              <div
                className={
                  'widget-demo-transparent' +
                  (this.state.widgetType === 'small'
                    ? ' mobile-transparent'
                    : '')
                }
              >
                {widgetSettingsLoaded && (
                  <DemoWidget
                    hideLanguageButtons={false}
                    changeSelectedView={this.changeSelectedView}
                    changeWidgetState={this.changeWidgetState}
                    changeSelectedLanguage={this.changeSelectedLanguage}
                    changeWidgetTypeShown={this.changeWidgetTypeShown}
                    widgetType={this.state.widgetType}
                  />
                )}
              </div>

              <Tabs
                selectedId={this.state.selectedTab}
                tabs={tabs}
                onChange={(selectedTab) => {
                  this.setState({ selectedTab });
                }}
              />
              {tabsBody[this.state.selectedTab]}
            </FlexBox>
          </ModalBody>
          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            cancelOnClick={this.closeModal}
          />
        </Form>
      </div>
    );
  }
}

export default reduxForm<WidgetCustomizationModalFormValuesT, PropsT>({
  form: FORM_NAME, // A unique identifier for this form.
  enableReinitialize: true,
  keepDirtyOnReinitialize: false,
  validate,
})(WidgetCustomizeModal);
